import { useCallback, useEffect, useState } from 'react';
import { subMonths, startOfMonth, endOfMonth, format } from 'date-fns';
import { supabase } from '../lib/supabaseClient';
import { getImpersonatedUserIdFromStorage } from '../contexts/ImpersonationContext';
import {
  buildPrevisionFinMoisFromAggregates,
  type PrevisionFinMoisContext,
} from '../lib/forecastTypes';

export type CategoryMonthTotals = Record<string, number>;

export interface IncomeTransactionItem {
  date: string;
  categorie: string;
  montant: number;
  note: string;
}

export interface ExpenseTransactionItem {
  date: string;
  categorie: string;
  montant: number;
  description: string;
}

export interface BudgetContextItem {
  categorie: string;
  montant_alloue: number;
  montant_consomme: number;
  pourcentage: number;
}

export interface ProjectContextItem {
  nom: string;
  objectif: number;
  montant_epargne: number;
  epargne_mensuelle: number;
  frequence: string;
  echeance_estimee: string | null;
}

export interface AccountContextItem {
  nom: string;
  type: string | null;
  solde: number;
  nom_banque: string | null;
  actif: boolean;
}

const EXPENSE_LOOKBACK_MONTHS = 3;
const INCOME_LOOKBACK_MONTHS = 24;
const MAX_INCOME_TRANSACTIONS = 50;
/** Plafond total pour Groq (~12k TPM) : historique long mais échantillonné. */
const MAX_EXPENSE_TRANSACTIONS = 120;
const MAX_EXPENSE_PER_MONTH = 8;

export interface FinancialContext {
  source: string;
  couverture_donnees: {
    depenses: string;
    revenus: string;
  };
  periodes: Record<string, string>;
  periodes_depenses: Record<string, string>;
  comptes: AccountContextItem[];
  solde_total_comptes: number;
  depenses_par_categorie: Record<string, CategoryMonthTotals>;
  revenus_par_categorie: Record<string, CategoryMonthTotals>;
  revenus_historique_par_categorie: Record<string, CategoryMonthTotals>;
  transactions_revenus: IncomeTransactionItem[];
  transactions_depenses: ExpenseTransactionItem[];
  total_depenses_3_mois: number;
  total_revenus_3_mois: number;
  revenu_mensuel_moyen: number;
  budgets: BudgetContextItem[];
  projets: ProjectContextItem[];
  prevision_fin_mois: PrevisionFinMoisContext;
}

interface TransactionRow {
  date: string;
  montant: number;
  type: string;
  categorie_id: string | null;
  description?: string | null;
}

interface CategoryRow {
  id: string;
  nom: string;
}

interface ProjectRow {
  project_name: string | null;
  target_amount: number;
  initial_saving: number | null;
  regular_saving: number;
  saving_frequency: string | null;
  created_at: string | null;
}

interface CompteRow {
  nom: string;
  solde: number;
  type: string | null;
  nom_banque: string | null;
  actif: boolean | null;
}

function monthKey(date: Date): string {
  return format(date, 'yyyy-MM');
}

function monthLabel(date: Date): string {
  return date
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    .replace(' ', '_');
}

function monthLabelFromYyyyMm(yyyyMm: string): string {
  const [year, month] = yyyyMm.split('-').map(Number);
  return monthLabel(new Date(year, month - 1, 1));
}

function buildPeriodInfo(now: Date, lookbackMonths: number) {
  const months = Array.from({ length: lookbackMonths }, (_, i) =>
    startOfMonth(subMonths(now, i)),
  ).reverse();

  const entries = months.map((date) => ({
    yyyyMm: monthKey(date),
    label: monthLabel(date),
  }));

  const yyyyMmToLabel = new Map(entries.map((e) => [e.yyyyMm, e.label]));
  const periodes = Object.fromEntries(entries.map((e) => [e.label, e.yyyyMm]));

  return { yyyyMmToLabel, periodes, labels: entries.map((e) => e.label) };
}

function resolveMonthLabel(
  txMonth: string,
  yyyyMmToLabel: Map<string, string>,
): string {
  const existing = yyyyMmToLabel.get(txMonth);
  if (existing) return existing;
  const label = monthLabelFromYyyyMm(txMonth);
  yyyyMmToLabel.set(txMonth, label);
  return label;
}

function formatCoverageRange(periodes: Record<string, string>): string {
  const values = Object.values(periodes);
  if (values.length === 0) return 'aucune';
  const sorted = [...values].sort();
  const first = monthLabelFromYyyyMm(sorted[0]);
  const last = monthLabelFromYyyyMm(sorted[sorted.length - 1]);
  return `${first.replace('_', ' ')} → ${last.replace('_', ' ')}`;
}

function expenseAmount(montant: number, type: string): number {
  if (type === 'depense') return Math.abs(montant);
  if (type === 'revenu') return 0;
  return montant < 0 ? Math.abs(montant) : 0;
}

function incomeAmount(montant: number, type: string): number {
  if (type === 'revenu') return Math.abs(montant);
  if (type === 'depense') return 0;
  return montant > 0 ? montant : 0;
}

/**
 * Priorise les libellés distinctifs (ex. "IKEA pour Elias") face au bruit bancaire
 * ("Transaction carte : LIDL…") — ne pas utiliser la seule longueur (les flux SEPA
 * sont souvent plus longs que les libellés saisis à la main).
 */
function expenseDescriptionScore(description: string): number {
  const d = (description || '').trim();
  if (!d) return -1;
  if (
    /^(Transaction carte\s*:|PRLV SEPA|RETRAIT DAB|VIR INST|WEB |Prelevement |Prélèvement )/i.test(
      d,
    )
  ) {
    return d.length;
  }
  if (/^(Courses |Essence |Don |Abonnement |Facture |Loyer )/i.test(d)) {
    return 100 + d.length;
  }
  return 1000 + d.length;
}

function expenseItemKey(item: ExpenseTransactionItem): string {
  return `${item.date}|${item.montant}|${item.categorie}|${item.description}`;
}

/**
 * Échantillonne les dépenses sur tout l'historique disponible :
 * 1) groupes de libellés distinctifs répétés (complets, ex. les 3 "IKEA pour Elias")
 * 2) max N par mois (score + montant), plafond global, tri date desc.
 */
function selectExpenseTransactionsForContext(
  items: ExpenseTransactionItem[],
  maxTotal: number,
  maxPerMonth: number,
): ExpenseTransactionItem[] {
  const descCounts = new Map<string, number>();
  for (const item of items) {
    const d = item.description.trim().toLowerCase();
    if (!d) continue;
    descCounts.set(d, (descCounts.get(d) || 0) + 1);
  }

  const selected: ExpenseTransactionItem[] = [];
  const selectedKeys = new Set<string>();

  // Phase 1 : conserver entiers les groupes rares et distinctifs (2–12 occurrences).
  const RARE_GROUP_MAX = 12;
  const rareItems = items
    .filter((item) => {
      const d = item.description.trim().toLowerCase();
      if (!d) return false;
      if (expenseDescriptionScore(item.description) < 1000) return false;
      const count = descCounts.get(d) || 0;
      return count >= 2 && count <= RARE_GROUP_MAX;
    })
    .sort((a, b) => {
      const amountDiff = Math.abs(b.montant) - Math.abs(a.montant);
      if (amountDiff !== 0) return amountDiff;
      return b.date.localeCompare(a.date);
    });

  for (const item of rareItems) {
    if (selected.length >= maxTotal) break;
    const key = expenseItemKey(item);
    if (selectedKeys.has(key)) continue;
    selected.push(item);
    selectedKeys.add(key);
  }

  // Phase 2 : complément stratifié par mois.
  const byMonth = new Map<string, ExpenseTransactionItem[]>();
  for (const item of items) {
    if (selectedKeys.has(expenseItemKey(item))) continue;
    const key = item.date.slice(0, 7);
    const list = byMonth.get(key);
    if (list) list.push(item);
    else byMonth.set(key, [item]);
  }

  const months = [...byMonth.keys()].sort((a, b) => b.localeCompare(a));

  for (const month of months) {
    if (selected.length >= maxTotal) break;
    const monthItems = byMonth.get(month) || [];
    monthItems.sort((a, b) => {
      const scoreDiff =
        expenseDescriptionScore(b.description) - expenseDescriptionScore(a.description);
      if (scoreDiff !== 0) return scoreDiff;
      const amountDiff = Math.abs(b.montant) - Math.abs(a.montant);
      if (amountDiff !== 0) return amountDiff;
      return b.date.localeCompare(a.date);
    });
    const room = maxTotal - selected.length;
    for (const item of monthItems.slice(0, Math.min(maxPerMonth, room))) {
      const key = expenseItemKey(item);
      if (selectedKeys.has(key)) continue;
      selected.push(item);
      selectedKeys.add(key);
    }
  }

  return selected.sort((a, b) => b.date.localeCompare(a.date));
}

function addToCategoryMonthTotals(
  map: Record<string, CategoryMonthTotals>,
  catName: string,
  monthLabelKey: string,
  amount: number,
): void {
  if (amount <= 0 || !monthLabelKey) return;

  if (!map[catName]) {
    map[catName] = {};
  }

  map[catName][monthLabelKey] =
    Math.round(((map[catName][monthLabelKey] || 0) + amount) * 100) / 100;
}

function estimateDeadline(
  target: number,
  saved: number,
  monthlySaving: number,
): string | null {
  const remaining = target - saved;
  if (remaining <= 0) return null;
  if (monthlySaving <= 0) return null;
  const months = Math.ceil(remaining / monthlySaving);
  const deadline = endOfMonth(subMonths(new Date(), -months));
  return format(deadline, 'yyyy-MM-dd');
}

async function resolveUserId(): Promise<string | null> {
  const impersonatedUserId = getImpersonatedUserIdFromStorage();
  if (impersonatedUserId) return impersonatedUserId;

  const { data: sessionData } = await supabase.auth.getSession();
  return sessionData?.session?.user?.id ?? null;
}

async function fetchTransactions(
  userId: string,
  startDate: string,
  endDate: string,
): Promise<TransactionRow[]> {
  const impersonatedUserId = getImpersonatedUserIdFromStorage();

  if (impersonatedUserId) {
    const rpc = await supabase.rpc('admin_get_transactions', { p_user_id: userId });
    if (rpc.error) throw rpc.error;
    const rows = (rpc.data as TransactionRow[]) || [];
    return rows.filter((t) => t.date >= startDate && t.date <= endDate);
  }

  const { data, error } = await supabase
    .from('transactions')
    .select('date, montant, type, categorie_id, description')
    .eq('user_id', userId)
    .gte('date', startDate)
    .lte('date', endDate);

  if (error) throw error;
  return (data as TransactionRow[]) || [];
}

async function fetchCategories(userId: string): Promise<Map<string, string>> {
  const impersonatedUserId = getImpersonatedUserIdFromStorage();
  let rows: CategoryRow[] = [];

  if (impersonatedUserId) {
    const rpc = await supabase.rpc('admin_get_categories', { p_user_id: userId });
    if (rpc.error) throw rpc.error;
    rows = (rpc.data as CategoryRow[]) || [];
  } else {
    const { data, error } = await supabase.from('categories').select('id, nom');
    if (error) throw error;
    rows = (data as CategoryRow[]) || [];
  }

  return new Map(rows.map((c) => [c.id, c.nom]));
}

async function fetchAccounts(userId: string): Promise<AccountContextItem[]> {
  const impersonatedUserId = getImpersonatedUserIdFromStorage();
  let rows: CompteRow[] = [];

  if (impersonatedUserId) {
    const rpc = await supabase.rpc('admin_get_comptes', { p_user_id: userId });
    if (rpc.error) throw rpc.error;
    rows = (rpc.data as CompteRow[]) || [];
  } else {
    const { data, error } = await supabase
      .from('comptes')
      .select('nom, solde, type, nom_banque, actif')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    rows = (data as CompteRow[]) || [];
  }

  return rows
    .filter((c) => c.actif !== false)
    .map((c) => ({
      nom: c.nom,
      type: c.type || null,
      solde: Math.round(Number(c.solde ?? 0) * 100) / 100,
      nom_banque: c.nom_banque || null,
      actif: c.actif !== false,
    }));
}

async function fetchProjects(userId: string): Promise<ProjectContextItem[]> {
  const { data, error } = await supabase
    .from('project_simulations')
    .select('project_name, target_amount, initial_saving, regular_saving, saving_frequency, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    if (error.code === '42P01' || error.code === 'PGRST205') return [];
    throw error;
  }

  return ((data as ProjectRow[]) || []).map((p) => {
    const saved = Number(p.initial_saving ?? 0);
    const monthly = Number(p.regular_saving ?? 0);
    const target = Number(p.target_amount ?? 0);
    return {
      nom: p.project_name || 'Projet sans nom',
      objectif: target,
      montant_epargne: saved,
      epargne_mensuelle: monthly,
      frequence: p.saving_frequency || 'mensuel',
      echeance_estimee: estimateDeadline(target, saved, monthly),
    };
  });
}

async function fetchBudgets(
  _userId: string,
  _categoryMap: Map<string, string>,
  currentMonthStart: string,
  _currentMonthEnd: string,
): Promise<BudgetContextItem[]> {
  try {
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    const { data, error } = await supabase.rpc('get_budgets_consommation', {
      p_mois: currentMonthStart,
      ...(impersonatedUserId ? { p_user_id: impersonatedUserId } : {}),
    });

    if (error) {
      if (error.code === '42P01' || error.code === 'PGRST205') return [];
      console.warn('[useFinancialContext] get_budgets_consommation:', error.message);
      return [];
    }

    const rows = (data as Array<{
      categorie_nom?: string;
      montant_alloue?: number;
      montant_depense?: number;
      pourcentage_consomme?: number;
    }> | null) ?? [];

    return rows
      .filter((r) => Number(r.montant_alloue ?? 0) > 0)
      .map((r) => {
        const alloue = Number(r.montant_alloue ?? 0);
        const depense = Number(r.montant_depense ?? 0);
        const pct = Number(r.pourcentage_consomme ?? 0);
        return {
          categorie: r.categorie_nom || 'Sans catégorie',
          montant_alloue: alloue,
          montant_consomme: depense,
          pourcentage: pct,
        };
      });
  } catch {
    return [];
  }
}

function buildFinancialContext(
  expenseTransactions: TransactionRow[],
  incomeTransactions: TransactionRow[],
  categoryMap: Map<string, string>,
  budgets: BudgetContextItem[],
  projets: ProjectContextItem[],
  comptes: AccountContextItem[],
): FinancialContext {
  const now = new Date();
  const expensePeriod = buildPeriodInfo(now, EXPENSE_LOOKBACK_MONTHS);
  const incomePeriod = buildPeriodInfo(now, INCOME_LOOKBACK_MONTHS);
  const expenseMonths = new Set(expensePeriod.yyyyMmToLabel.keys());

  const depenses_par_categorie: Record<string, CategoryMonthTotals> = {};
  const revenus_par_categorie: Record<string, CategoryMonthTotals> = {};
  const revenus_historique_par_categorie: Record<string, CategoryMonthTotals> = {};
  const incomeByMonth: Record<string, number> = Object.fromEntries(
    expensePeriod.labels.map((label) => [label, 0]),
  );
  const incomeTransactionsList: IncomeTransactionItem[] = [];
  const expenseTransactionsList: ExpenseTransactionItem[] = [];

  let total_depenses_3_mois = 0;
  let total_revenus_3_mois = 0;

  // Agrégats dépenses : fenêtre courte (3 mois)
  for (const tx of expenseTransactions) {
    if (tx.type === 'transfert') continue;

    const txMonth = tx.date.slice(0, 7);
    if (!expenseMonths.has(txMonth)) continue;

    const catName =
      (tx.categorie_id && categoryMap.get(tx.categorie_id)) || 'Non catégorisé';
    const montant = Number(tx.montant);
    const expense = expenseAmount(montant, tx.type);

    if (expense > 0) {
      total_depenses_3_mois += expense;
      const label = resolveMonthLabel(txMonth, expensePeriod.yyyyMmToLabel);
      addToCategoryMonthTotals(depenses_par_categorie, catName, label, expense);
    }
  }

  // Liste individuelle dépenses : même historique long que les revenus (24 mois)
  for (const tx of incomeTransactions) {
    if (tx.type !== 'depense') continue;

    const catName =
      (tx.categorie_id && categoryMap.get(tx.categorie_id)) || 'Non catégorisé';
    const expense = expenseAmount(Number(tx.montant), tx.type);
    if (expense <= 0) continue;

    expenseTransactionsList.push({
      date: tx.date,
      categorie: catName,
      montant: -Math.round(expense * 100) / 100,
      description: (tx.description && tx.description.trim()) || '',
    });
  }

  for (const tx of incomeTransactions) {
    if (tx.type === 'transfert') continue;

    const txMonth = tx.date.slice(0, 7);
    const catName =
      (tx.categorie_id && categoryMap.get(tx.categorie_id)) || 'Non catégorisé';
    const montant = Number(tx.montant);
    const income = incomeAmount(montant, tx.type);

    if (income <= 0) continue;

    const label = resolveMonthLabel(txMonth, incomePeriod.yyyyMmToLabel);
    addToCategoryMonthTotals(revenus_historique_par_categorie, catName, label, income);

    if (expenseMonths.has(txMonth)) {
      total_revenus_3_mois += income;
      if (incomeByMonth[label] != null) {
        incomeByMonth[label] += income;
      }
      addToCategoryMonthTotals(revenus_par_categorie, catName, label, income);
    }

    incomeTransactionsList.push({
      date: tx.date,
      categorie: catName,
      montant: Math.round(income * 100) / 100,
      note: (tx.description && tx.description.trim()) || '',
    });
  }

  const transactions_revenus = incomeTransactionsList
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, MAX_INCOME_TRANSACTIONS);

  const transactions_depenses = selectExpenseTransactionsForContext(
    expenseTransactionsList,
    MAX_EXPENSE_TRANSACTIONS,
    MAX_EXPENSE_PER_MONTH,
  );

  const monthlyIncomes = Object.values(incomeByMonth);
  const revenu_mensuel_moyen =
    monthlyIncomes.length > 0
      ? Math.round((monthlyIncomes.reduce((a, b) => a + b, 0) / monthlyIncomes.length) * 100) / 100
      : 0;

  const solde_total_comptes =
    Math.round(comptes.reduce((sum, c) => sum + c.solde, 0) * 100) / 100;

  // Prévision IA (calcul local, sans RPC)
  const depensesByMonthYyyyMm: Record<string, number> = {};
  const revenusByMonthYyyyMm: Record<string, number> = {};
  const depensesParCatYyyyMm: Record<string, Record<string, number>> = {};

  for (const tx of expenseTransactions) {
    const txMonth = tx.date.slice(0, 7);
    const expense = expenseAmount(Number(tx.montant), tx.type);
    if (expense > 0) {
      depensesByMonthYyyyMm[txMonth] = (depensesByMonthYyyyMm[txMonth] || 0) + expense;
      const catName =
        (tx.categorie_id && categoryMap.get(tx.categorie_id)) || 'Non catégorisé';
      if (!depensesParCatYyyyMm[catName]) depensesParCatYyyyMm[catName] = {};
      depensesParCatYyyyMm[catName][txMonth] =
        (depensesParCatYyyyMm[catName][txMonth] || 0) + expense;
    }
  }
  for (const tx of incomeTransactions) {
    const txMonth = tx.date.slice(0, 7);
    const income = incomeAmount(Number(tx.montant), tx.type);
    if (income > 0) {
      revenusByMonthYyyyMm[txMonth] = (revenusByMonthYyyyMm[txMonth] || 0) + income;
    }
  }

  const prevision_fin_mois = buildPrevisionFinMoisFromAggregates({
    now,
    depensesByMonth: depensesByMonthYyyyMm,
    revenusByMonth: revenusByMonthYyyyMm,
    depensesParCategorie: depensesParCatYyyyMm,
    budgets,
  });

  return {
    source:
      'Données saisies dans SuiviFinance (pas de connexion bancaire directe). Comptes, soldes, transactions agrégées, budgets et projets.',
    couverture_donnees: {
      depenses: `${EXPENSE_LOOKBACK_MONTHS} derniers mois pour les totaux (${formatCoverageRange(expensePeriod.periodes)}) ; transactions_depenses = détail individuel échantillonné sur ${INCOME_LOOKBACK_MONTHS} mois (libellés prioritaires, max ${MAX_EXPENSE_TRANSACTIONS})`,
      revenus: `${INCOME_LOOKBACK_MONTHS} derniers mois (${formatCoverageRange(incomePeriod.periodes)}) — utiliser revenus_historique_par_categorie pour les questions sur une année passée`,
    },
    periodes: incomePeriod.periodes,
    periodes_depenses: expensePeriod.periodes,
    comptes,
    solde_total_comptes,
    depenses_par_categorie,
    revenus_par_categorie,
    revenus_historique_par_categorie,
    transactions_revenus,
    transactions_depenses,
    total_depenses_3_mois: Math.round(total_depenses_3_mois * 100) / 100,
    total_revenus_3_mois: Math.round(total_revenus_3_mois * 100) / 100,
    revenu_mensuel_moyen,
    budgets,
    projets,
    prevision_fin_mois,
  };
}

export function useFinancialContext() {
  const [context, setContext] = useState<FinancialContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const userId = await resolveUserId();
      if (!userId) {
        setError('Session non connectée');
        setContext(null);
        return;
      }

      const now = new Date();
      const expenseStartDate = format(
        startOfMonth(subMonths(now, EXPENSE_LOOKBACK_MONTHS - 1)),
        'yyyy-MM-dd',
      );
      const incomeStartDate = format(
        startOfMonth(subMonths(now, INCOME_LOOKBACK_MONTHS - 1)),
        'yyyy-MM-dd',
      );
      const endDate = format(endOfMonth(now), 'yyyy-MM-dd');
      const currentMonthStart = format(startOfMonth(now), 'yyyy-MM-dd');
      const currentMonthEnd = format(endOfMonth(now), 'yyyy-MM-dd');

      const [expenseTransactions, incomeTransactions, categoryMap, projets, comptes] =
        await Promise.all([
          fetchTransactions(userId, expenseStartDate, endDate),
          fetchTransactions(userId, incomeStartDate, endDate),
          fetchCategories(userId),
          fetchProjects(userId),
          fetchAccounts(userId),
        ]);

      const budgets = await fetchBudgets(
        userId,
        categoryMap,
        currentMonthStart,
        currentMonthEnd,
      );

      setContext(
        buildFinancialContext(
          expenseTransactions,
          incomeTransactions,
          categoryMap,
          budgets,
          projets,
          comptes,
        ),
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Erreur lors du chargement du contexte financier';
      setError(message);
      setContext(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onImpersonationChanged = () => {
      void refresh();
    };
    window.addEventListener('suivifinance:impersonation-changed', onImpersonationChanged);
    return () =>
      window.removeEventListener('suivifinance:impersonation-changed', onImpersonationChanged);
  }, [refresh]);

  return { context, loading, error, refresh };
}
